import { Popover } from '@/components/utils/Popover/Popover';
import { capture } from '@/handlers/error';
import { useQuery } from '@apollo/client';
import SearchIcon from '@mui/icons-material/Search';
import { Box, CircularProgress, FormControl, InputAdornment, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useNotifications } from '@toolpad/core';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'rooks';
import { GET_BASIC_STROKES, GetBasicStrokesResponse, GetBasicStrokesVariables } from './gql';

const SIZE = {
    width: 440,
    height: 640,
};

type StrokePickerProps = {
    code: string;
    locale: string;
    character: string;
};

interface Props {
    title: string;
    anchorEl: HTMLElement | null;
    value: StrokePickerProps;
    onClose: () => void;
    onUpdate: (settings: StrokePickerProps) => void;
    slotProps?: { paper?: { ref: React.Ref<HTMLDivElement> } };
}

export const StrokePicker: React.FC<Props> = ({ title, anchorEl, onClose, onUpdate, value, slotProps }) => {
    const { t } = useTranslation();
    const notifications = useNotifications();

    const { data: searchRes, refetch: getBasicStrokes } = useQuery<GetBasicStrokesResponse, GetBasicStrokesVariables>(GET_BASIC_STROKES, {
        variables: {
            where: {
                character: {
                    contains: '',
                },
                locale: {
                    equals: 'zh-HK',
                },
            },
        },
        fetchPolicy: 'cache-and-network',
    });

    const open = Boolean(anchorEl);

    const [searchText, setSearchText] = useState('');
    const [selectedLanguage, setSelectedLanguage] = useState('zh-HK');
    const [searching, setSearching] = useState(false);

    const onSearchChange = React.useCallback(
        async (value: string, locale: string) => {
            setSearchText(value);

            setSearching(true);
            try {
                await getBasicStrokes({
                    where: {
                        character: {
                            contains: value ?? '',
                        },
                        locale: {
                            equals: locale,
                        },
                    },
                });
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                capture(error);
            } finally {
                setSearching(false);
            }
        },
        [getBasicStrokes, notifications],
    );

    const debouncedSearchChange = useDebounce(onSearchChange, 500);

    return (
        <Popover
            title={title}
            open={open}
            anchorEl={anchorEl}
            onClose={() => {
                onClose();
            }}
            anchorOrigin={{
                vertical: 'center',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'center',
                horizontal: -8,
            }}
            slotProps={{
                paper: {
                    ref: slotProps?.paper?.ref,
                    sx: {
                        width: SIZE.width,
                        height: SIZE.height,
                        display: 'flex',
                        p: 2,
                    },
                },
            }}
        >
            <Stack spacing={1.5}>
                <TextField
                    label={t('slide.components.assets.image.search')}
                    autoFocus
                    fullWidth
                    onChange={(e) => {
                        debouncedSearchChange(e.target.value, selectedLanguage);
                    }}
                    defaultValue={searchText}
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position='end'>
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        },
                    }}
                />

                <FormControl sx={{ display: 'flex', alignItems: 'center' }}>
                    <ToggleButtonGroup
                        value={selectedLanguage}
                        exclusive
                        fullWidth
                        onChange={(_e, value) => {
                            if (value) {
                                debouncedSearchChange(searchText, value);
                                setSelectedLanguage(value);
                            }
                        }}
                        sx={{ justifyContent: 'space-evenly' }}
                    >
                        <ToggleButton value='zh-HK' fullWidth>
                            {t('slide.components.picker.word.traditionalChinese')}
                        </ToggleButton>
                        <ToggleButton value='zh-CN' fullWidth>
                            {t('slide.components.picker.word.simplifiedChinese')}
                        </ToggleButton>
                    </ToggleButtonGroup>
                </FormControl>
            </Stack>

            <Box
                sx={(theme) => ({
                    mt: 2,
                    flex: 1,
                    display: 'flex',
                    alignContent: 'center',
                    overflow: 'auto',
                    backgroundColor: (theme.vars || theme).palette.grey[200],
                    ...theme.applyStyles('dark', {
                        backgroundColor: (theme.vars || theme).palette.grey[700],
                    }),
                })}
            >
                <Box
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        display: 'flex',
                        px: 1,
                        my: 1.5,
                    }}
                >
                    {searchText === undefined ? (
                        <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
                            <SearchIcon fontSize='small' />
                            <Typography>{t('slide.components.picker.word.placeholder')}</Typography>
                        </Box>
                    ) : searching ? (
                        <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                            <CircularProgress />
                        </Box>
                    ) : searchRes && searchRes.basicStrokes.length <= 0 ? (
                        <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                            <Typography>{t('slide.components.assets.image.noResultFor', { keyword: searchText })}</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ flex: 1 }}>
                            <Grid container spacing={1} sx={{ justifyContent: 'space-evenly' }}>
                                {searchRes &&
                                    searchRes.basicStrokes.map((item) => (
                                        <Grid
                                            key={item.id}
                                            sx={{
                                                position: 'relative',
                                                width: 120,
                                                height: 120,
                                                backgroundColor: (theme) => (theme.vars || theme).palette.grey[50],
                                                borderRadius: 1,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: '2px solid',
                                                borderColor: 'transparent',
                                                '&:hover': {
                                                    opacity: 0.8,
                                                    border: '2px solid',
                                                    borderColor: 'primary.main',
                                                },
                                                p: 1,
                                            }}
                                            onClick={() => {
                                                onUpdate({
                                                    ...value,
                                                    code: item.code,
                                                    locale: item.locale,
                                                    character: item.character,
                                                });
                                                onClose();
                                            }}
                                        >
                                            <Box component='img' src={`/asset/strokes/svg/${item.code}_${item.character}/${item.character}.svg`} width={120} height={120} />
                                        </Grid>
                                    ))}

                                {searchRes &&
                                    searchRes.basicStrokes.length % 3 !== 0 &&
                                    // Fill the remaining space with empty boxes just to make it looks nice with space-evenly
                                    Array.from({ length: 3 - (searchRes.basicStrokes.length % 3) }).map((_, index) => (
                                        <Grid
                                            key={`empty-${index}`}
                                            sx={{
                                                width: 120,
                                                height: 120,
                                                backgroundColor: 'transparent',
                                                borderRadius: 1,
                                            }}
                                        />
                                    ))}
                            </Grid>
                        </Box>
                    )}
                </Box>
            </Box>
        </Popover>
    );
};
